<template>
  <div class="rule-stats">
    <div class="rule-stat">
      <span class="icon">
        <icon-colored-targeting />
      </span>
      <p v-tooltip.bottom="i18n.t('messages.reach')">
        {{ capitalize(i18n.t('names.reach')) }}: <strong>{{ !reach ? DASH : i18n.n(reach) }}</strong>
      </p>
    </div>
    <div class="rule-stat">
      <span class="icon">
        <icon-colored-c-p-m />
      </span>
      <p>
        {{ capitalize(i18n.t('names.cpm')) }}: <strong>{{ !cpm ? DASH : i18n.n(cpm, 'currency') }}</strong>
      </p>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { capitalize, computed, onBeforeMount, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import DemographicCategory from '@/common/enums/DemographicCategory.enum';
  import { IconColoredCPM, IconColoredTargeting } from '@/common/icons/colored';
  import { Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import ExternalSegment from '@/common/models/audience/ExternalSegment';
  import { DASH, uniqBy } from '@/globals';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import { mapOrgAppActions } from '@/utils/StateMapping';

  //
  // Setup
  //
  const i18n = useI18n();
  const { getAllDataProviders, getLifesightAudienceSegmentsByOrg } = mapOrgAppActions();
  const props = defineProps<{
    rule: AudienceRule;
  }>();

  //
  // Data
  //
  const reach = ref<number>();
  const cpm = ref<number>();
  const isRuleComplete = computed(() => AudienceSegmentUtils.isAudienceRuleComplete(props.rule));

  //
  // Segments
  //
  const demographicSegments = ref<ExternalSegment[]>([]);
  const targetedDemographicSegments = computed<ExternalSegment[]>(() => {
    if (props.rule.mode !== AudienceRuleModes.PredefinedDemographic) return [];
    if (!props.rule.segments) return [];
    return props.rule.segments
      .map((s) => s.demographicId)
      .map((id) => demographicSegments.value.find((s) => s.demographicId === id));
  });

  //
  // Functions
  //
  async function getAllLifesightDemographicSegments() {
    if (props.rule.mode !== AudienceRuleModes.PredefinedDemographic) return [];
    const dataProviders = await getAllDataProviders();
    const filter = new Filter('external_id', ['LSD'], FilterComparisonOperator.LIKE);
    const qp = new QueryParams(1750, 0, new FilterCollection([filter]), 'reach desc', true, ['location']);
    qp.customQueryParams.dataProviderId = dataProviders.find(isLifesightDataProvider).id;
    const { items: allSegments } = await qp.run(getLifesightAudienceSegmentsByOrg);
    return allSegments;
  }
  // CPM Calculation
  async function calculateReachAndCPM() {
    switch (props.rule.mode) {
      case AudienceRuleModes.Epsilon:
        if (isRuleComplete.value) calculateEpsilonReachAndCPM();
        break;
      case AudienceRuleModes.Segments:
        if (isRuleComplete.value) calculateLifesightReachAndCPM();
        break;
      case AudienceRuleModes.PredefinedDemographic:
        await calculateDemographicReachAndCPM();
        break;
      default:
        break;
    }
  }
  async function calculateDemographicReachAndCPM() {
    const countries = uniqBy(
      targetedDemographicSegments.value.length ? targetedDemographicSegments.value : demographicSegments.value,
      'country',
    ).map((s) => s.country);
    const countrySegments = demographicSegments.value.filter((s) => countries.includes(s.country));
    const genderSegments = targetedDemographicSegments.value.filter(
      (segment) => segment.category === DemographicCategory.Gender,
    );
    const allGenderSegments = countrySegments.filter((segment) => segment.category === DemographicCategory.Gender);
    const ageSegments = targetedDemographicSegments.value.filter(
      (segment) => segment.category === DemographicCategory.Age,
    );
    const allAgeSegments = countrySegments.filter((segment) => segment.category === DemographicCategory.Age);
    const allTargetedSegments = [
      ...(genderSegments.length ? genderSegments : allGenderSegments),
      ...(ageSegments.length ? ageSegments : allAgeSegments),
    ];
    reach.value = AudienceSegmentUtils.calculateReachForDemographicsAudience(allTargetedSegments, allGenderSegments);
  }
  function calculateEpsilonReachAndCPM() {
    const segment = props.rule.segment;
    if (segment) {
      reach.value = segment.reach;
      cpm.value = segment.cpm;
    }
  }
  function calculateLifesightReachAndCPM() {
    const segment = props.rule.segment;
    if (segment) {
      reach.value = segment.reach;
      cpm.value = segment.cpm;
    }
  }

  //
  // Lifecycle
  //
  onBeforeMount(async () => {
    demographicSegments.value = await getAllLifesightDemographicSegments();
    await calculateReachAndCPM();
  });
  watch(
    () => props.rule.name,
    async () => {
      await calculateReachAndCPM();
    },
  );
</script>

<style lang="scss" scoped>
  .rule-stats {
    display: flex;
    gap: 2rem;
    margin-top: 1rem;

    .rule-stat {
      display: flex;
      gap: 0.4rem;
      align-items: center;

      p {
        margin-bottom: 0 !important;
      }

      .icon {
        width: 2rem;
        height: 2rem;
      }
    }
  }
</style>
